/**
 * SF-4652: review a demo build's change set (/demos/:id/review). The set on the left, grouped with
 * counts; on the right a chat with Eva to change it, then approve (builds exactly the version on
 * screen) or cancel (stops before anything is made). While Eva is drafting the page re-reads the
 * build every few seconds and highlights what her new version changed.
 * SF-4746: someone else's build (All demos) is read only: its set and chat show, every action
 * (send, approve, cancel, draft again, dismiss) is hidden. A build past its review (opened from
 * "View plan and chat") shows its set and chat the same way, with no box to send.
 * SF-4847: while the plan waits for review its creator can take out modules, locations and users
 * (an X on each); any removal swaps Approve for "Make version N", which asks Eva for a new version.
 * Send and Enter in the chat box mean the same as Make version while anything is taken out. While
 * a message, approval or cancel is on its way the X, Undo, Make version and Approve wait for it.
 * A version that still has items taken out before (build.inputs.removed) shows them struck
 * through with no Undo and offers only Make version (sf-api refuses to approve it, S6).
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCan } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Result, Row, Space, Spin, Typography } from 'antd';
import { EnvironmentTag } from './components/EnvironmentBadge';
import { StatusTag } from './components/StatusTag';
import { ChangeSetView } from './components/ChangeSetView';
import { ReviewChat } from './components/ReviewChat';
import { ApprovePanel } from './components/ApprovePanel';
import { MakeVersionPanel } from './components/MakeVersionPanel';
import { DraftAgain } from './components/DraftAgain';
import { DismissDemo } from './components/DismissDemo';
import { ReadOnlyNote } from './components/ReadOnlyNote';
import { useReviewConversation } from './useReviewConversation';
import { isProduction } from '../../config/env';
import { demoDetailPath, isPastReview, startedBy } from './constants';
import { useCanActOn } from './useCanActOn';
import {
  CHANGE_SET_STALE,
  CHANGE_SET_STALE_TEXT,
  PLAN_HAS_REMOVED_ITEMS,
  PLAN_HAS_REMOVED_ITEMS_TEXT,
  approveArgs,
  cancelArgs,
  demoBuildQueryKey,
  demoErrorCode,
  demoErrorMessage,
  sendMessageArgs,
  sendRemovalsArgs,
  useDemoBuild,
  useDemoBuildMessages,
  useDemoMutation,
  type DemoBuild,
  type DemoBuildWithChangeSet,
} from './api/demoBuildsClient';

/** How often the page re-reads the build while Eva is drafting. */
export const REVIEW_POLL_MS = 3000;
const CANCELLABLE = ['awaiting_transcripts', 'drafting', 'ready_for_review'];

const statusNote = (b: DemoBuildWithChangeSet): React.ReactNode => {
  switch (b.status) {
    case 'awaiting_transcripts':
      return (
        <Alert type="info" showIcon message="Waiting for the call transcripts before Eva drafts." />
      );
    case 'drafting':
      return b.change_set ? null : (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          message="Eva is drafting the change set. This page updates by itself."
        />
      );
    case 'cancelled':
      return (
        <Alert type="info" showIcon message="This demo was cancelled before anything was made." />
      );
    case 'failed':
      return (
        <Alert
          type="error"
          showIcon
          message="This demo build stopped"
          description={b.error ?? undefined}
        />
      );
    case 'building':
    case 'done':
      return null;
    default:
      return null;
  }
};

function ReviewBody({ id }: { id: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Polling follows the cached status, so a failed read keeps it going while Eva drafts.
  const query = useDemoBuild<DemoBuildWithChangeSet>(id, true, (b) =>
    b?.status === 'drafting' ? REVIEW_POLL_MS : false,
  );
  const build = query.data?.data;
  const status = build?.status;

  /**
   * A POST sf-api accepted answers with the build as it now is (toView). Put that in the detail
   * cache first, dropping any read already under way from before the POST, then re-read. If that
   * read fails the page still shows the new state (drafting keeps polling; approve stays off).
   */
  const applyBuild = async (answer: unknown) => {
    const next = (answer as { data?: Partial<DemoBuildWithChangeSet> } | undefined)?.data;
    if (next?.id === id && next.status) {
      const key = demoBuildQueryKey(id);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<{ data: DemoBuildWithChangeSet }>(key, (old) =>
        old ? { ...old, data: { ...old.data, ...next } } : old,
      );
    }
    void query.refetch();
  };

  const changeSet = build?.change_set ?? null;
  const version = build?.change_set_version ?? 0;
  // The chat as sf-api holds it, read on opening the page and again when Eva has said more; if a
  // read fails the page keeps the last chat it read, its own lines not yet saved, and a Retry.
  const historyQuery = useDemoBuildMessages(id, true);
  const historyData = historyQuery.data?.data;
  const history = useMemo(
    () => (Array.isArray(historyData?.messages) ? historyData.messages : null),
    [historyData],
  );
  const { diff, lines, said, versionNote } = useReviewConversation(
    changeSet,
    version,
    status,
    history,
    historyQuery.reread,
  );

  const message = useDemoMutation();
  const approve = useDemoMutation();
  const cancel = useDemoMutation();
  const [chatError, setChatError] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<React.ReactNode>(null);
  // SF-4847: remove_ids taken out on this page, not yet sent; the chat box's text goes with them.
  const [pending, setPending] = useState<string[]>([]);
  const [chatText, setChatText] = useState('');
  const [makeError, setMakeError] = useState<string | null>(null);
  const canActOn = useCanActOn();

  if (query.isLoading) return <Spin />;
  if (!build) {
    return (
      <Alert
        type="error"
        showIcon
        message={demoErrorMessage(query.error, 'Could not load this demo.')}
      />
    );
  }

  const production = isProduction(build.environment);
  const readOnly = !canActOn(build);
  const ready = status === 'ready_for_review' && !!changeSet;
  // Only ids the set on screen still has count: a version Eva made since may not list them.
  const removable = new Set(
    changeSet
      ? [
          ...(changeSet.modules ?? []).map((m) => m.remove_id),
          ...(changeSet.groups ?? []).flatMap((g) => (g.items ?? []).map((i) => i.remove_id)),
        ].filter((r): r is string => !!r)
      : [],
  );
  // S6: items taken out before that Eva's version on screen still has (an item, or a module row
  // still on). They are already pending: struck through, no Undo, and only Make version is offered.
  const removedBefore = new Set(build.inputs?.removed ?? []);
  const locked = changeSet
    ? [
        ...(changeSet.modules ?? []).filter((m) => m.on).map((m) => m.remove_id),
        ...(changeSet.groups ?? []).flatMap((g) => (g.items ?? []).map((i) => i.remove_id)),
      ].filter((r): r is string => !!r && removedBefore.has(r))
    : [];
  const livePending = pending.filter((r) => removable.has(r) && !locked.includes(r));
  // Everything the next version must leave out; any of it means the plan cannot be approved.
  const toSend = [...locked, ...livePending];
  const edited = toSend.length > 0;
  // Any write on its way freezes the plan's edits, so what was sent is what the page shows.
  const busy = message.isLoading || approve.isLoading || cancel.isLoading;
  const canRemove = ready && !readOnly && Array.isArray(changeSet?.modules);
  const removals = canRemove
    ? {
        pending: livePending,
        locked,
        disabled: busy,
        onRemove: (r: string) => {
          if (!busy) setPending((p) => (p.includes(r) ? p : [...p, r]));
        },
        onUndo: (r: string) => {
          if (!busy) setPending((p) => p.filter((x) => x !== r));
        },
      }
    : null;

  const send = async (text: string) => {
    // One meaning for submitting an edited plan: Send and Enter go with the removals too.
    if (removals && edited) return makeVersion(text);
    setChatError(null);
    try {
      const clientId = crypto.randomUUID();
      const answer = await message.mutateAsync(sendMessageArgs(id, text, clientId));
      said(text, clientId);
      void applyBuild(answer);
      return true;
    } catch (e) {
      setChatError(demoErrorMessage(e, 'Eva could not take that message. Try again.'));
      void query.refetch();
      return false;
    }
  };

  async function makeVersion(typed?: string): Promise<boolean> {
    if (!changeSet || !edited || busy) return false;
    setMakeError(null);
    setChatError(null);
    const text = (typed ?? chatText).trim();
    const sent = toSend;
    try {
      const clientId = crypto.randomUUID();
      const answer = await message.mutateAsync(
        sendRemovalsArgs(id, { text, clientId, version, removed: sent }),
      );
      // sf-api saves its own wording when nothing was typed (SF-4847 brief S3); show the same.
      said(text || 'Take out the items I removed.', clientId);
      setPending((p) => p.filter((r) => !sent.includes(r)));
      setChatText('');
      void applyBuild(answer);
      return true;
    } catch (e) {
      setMakeError(
        demoErrorMessage(
          e,
          demoErrorCode(e) === CHANGE_SET_STALE
            ? CHANGE_SET_STALE_TEXT
            : 'Eva could not take that change. Try again.',
        ),
      );
      // A stale plan (another tab made a version) or a refusal: show the build as it now is.
      void query.refetch();
      return false;
    }
  }

  const onApprove = (confirmName?: string) => {
    // The version and hash both come from the change set this render shows, so sf-api can refuse
    // the approval if the plan behind that version number is no longer the one on screen.
    if (!changeSet) return;
    setApproveError(null);
    const shownVersion = version;
    approve.mutate(
      approveArgs(id, {
        change_set_version: shownVersion,
        change_set_hash: changeSet.hash,
        ...(production ? { confirm_company_name: confirmName } : {}),
      }),
      {
        onSuccess: (answer) => void applyBuild(answer),
        onError: (e) => {
          const stale = demoErrorCode(e) === CHANGE_SET_STALE;
          if (demoErrorCode(e) === PLAN_HAS_REMOVED_ITEMS) {
            setApproveError(
              <Alert
                type="error"
                showIcon
                data-testid="approve-removed-items"
                message="Not approved"
                description={PLAN_HAS_REMOVED_ITEMS_TEXT}
              />,
            );
            void query.refetch();
            return;
          }
          setApproveError(
            <Alert
              type="error"
              showIcon
              data-testid={stale ? 'approve-stale' : 'approve-error'}
              message={
                stale
                  ? `Not approved: version ${shownVersion} is no longer the latest`
                  : 'Not approved'
              }
              description={
                stale
                  ? 'Eva changed the set after this version, so nothing was built. The page now shows the latest version: check it, then approve again.'
                  : demoErrorMessage(e, 'The demo could not be approved. Try again.')
              }
            />,
          );
          void query.refetch();
        },
      },
    );
  };

  const onCancel = () => {
    setApproveError(null);
    cancel.mutate(cancelArgs(id), {
      onSuccess: (answer) => void applyBuild(answer),
      onError: (e) => {
        setApproveError(
          <Alert
            type="error"
            showIcon
            data-testid="cancel-error"
            message={demoErrorMessage(e, 'The demo could not be cancelled.')}
          />,
        );
        void query.refetch();
      },
    });
  };

  const started = status === 'building' || status === 'done';
  const onBuild = (_buildId: string, answer?: DemoBuild) =>
    applyBuild(answer ? { data: answer } : undefined);
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space wrap>
            <span>Review the demo for {build.inputs?.domain}</span>
            <StatusTag status={build.status} dismissed={!!build.dismissed_at} />
            <EnvironmentTag env={build.environment} />
          </Space>
        }
        extra={<Button onClick={() => navigate('/demos')}>Back to demos</Button>}
      >
        {!build.is_mine && (
          <div style={{ marginBottom: 16 }}>
            <ReadOnlyNote creator={build.created_by} />
          </div>
        )}
        {statusNote(build)}
        {!readOnly && build.can_draft_again && (
          <div style={{ marginTop: 16 }}>
            <DraftAgain key={id} buildId={id} onBuild={onBuild} />
          </div>
        )}
        {!readOnly && build.can_dismiss && (
          <div style={{ marginTop: 16 }}>
            <DismissDemo key={id} buildId={id} onBuild={onBuild} />
          </div>
        )}
        {started && (
          <Alert
            type="success"
            showIcon
            message={
              status === 'done'
                ? 'The demo is built from this version.'
                : 'Approved. The demo is being built from this version.'
            }
            action={<Button onClick={() => navigate(demoDetailPath(id))}>See progress</Button>}
          />
        )}
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          {changeSet ? (
            <ChangeSetView
              changeSet={changeSet}
              version={version}
              build={build}
              diff={diff}
              removals={removals}
            />
          ) : (
            <Typography.Text type="secondary">There is no change set to show yet.</Typography.Text>
          )}
        </Col>
        <Col xs={24} lg={9}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ReviewChat
              readOnly={readOnly || isPastReview(build.status)}
              youLabel={build.is_mine ? undefined : startedBy(build.created_by)}
              lines={lines}
              working={status === 'drafting'}
              closedReason={
                ready || status === 'drafting'
                  ? null
                  : 'Eva can only change the set while it waits for your review.'
              }
              error={chatError}
              sending={message.isLoading}
              onSend={send}
              text={chatText}
              onTextChange={setChatText}
            />
            {historyQuery.isError && (
              <Typography.Text type="secondary" data-testid="history-error">
                Couldn't load the saved chat.{' '}
                <Button type="link" size="small" onClick={() => void historyQuery.reread()}>
                  Retry
                </Button>
              </Typography.Text>
            )}
            {versionNote && (
              <Typography.Text type="secondary" data-testid="version-note">
                {versionNote}
              </Typography.Text>
            )}
            {removals && edited && (
              <MakeVersionPanel
                version={version}
                count={toSend.length}
                canSend={ready && !busy}
                sending={message.isLoading}
                error={makeError}
                stale={locked.length > 0}
                onMake={() => void makeVersion()}
                canCancel={CANCELLABLE.includes(build.status) && !busy}
                cancelling={cancel.isLoading}
                onCancel={onCancel}
                actionError={approveError}
              />
            )}
            {!readOnly &&
              !started &&
              status !== 'cancelled' &&
              status !== 'failed' &&
              !(removals && edited) && (
              <ApprovePanel
                version={version}
                production={production}
                companyName={changeSet?.company_name || null}
                canApprove={ready && !busy}
                canCancel={CANCELLABLE.includes(build.status)}
                approving={approve.isLoading}
                cancelling={cancel.isLoading}
                error={approveError}
                onApprove={onApprove}
                onCancel={onCancel}
              />
            )}
            {(status === 'cancelled' || status === 'failed') && approveError}
          </Space>
        </Col>
      </Row>
    </Space>
  );
}

export const DemoReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: can, isLoading } = useCan({ resource: 'demos', action: 'show' });
  if (isLoading) return <Spin />;
  if (can?.can !== true) {
    return <Result status="403" title="403" subTitle="You do not have permission to see demos." />;
  }
  return id ? <ReviewBody key={id} id={id} /> : null;
};
